"use client";

import { useEffect } from "react";
import { X } from "lucide-react";
import { youtubeEmbedUrl } from "@/lib/v2/video";

/** Full-screen YouTube player. Pass `id` null to keep it closed. */
export function VideoLightbox({ id, onClose, label }: { id: string | null; onClose: () => void; label: string }) {
  useEffect(() => {
    if (!id) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id, onClose]);

  if (!id) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 p-4 md:p-8"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={label} className="relative w-full max-w-6xl">
        <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
          <iframe
            src={youtubeEmbedUrl(id)}
            title={label}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="size-full"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-14 right-0 rounded-full bg-white p-3 text-ink shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:-right-14 md:top-0"
        >
          <X aria-hidden="true" className="size-5" />
        </button>
      </div>
    </div>
  );
}
